const KEY = 'strudel-harness:anthropic-key:v1';
const MODEL_KEY = 'strudel-harness:agent-model:v1';
const EFFORT_KEY = 'strudel-harness:agent-effort:v1';

export type ModelId =
  | 'claude-opus-4-7'
  | 'claude-opus-4-6'
  | 'claude-sonnet-4-6'
  | 'claude-haiku-4-5';

// 'off' disables both adaptive thinking and output_config.effort on the
// API call. Distinct from `null`, which means the model has no effort
// knob at all (Haiku 4.5).
export type EffortLevel = 'off' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export type ModelCapabilities = {
  /** The model supports thinking:{type:'adaptive'}. */
  adaptiveThinking: boolean;
  /** Effort values supported by this model. Empty array = effort not
   *  supported (Haiku 4.5). */
  efforts: EffortLevel[];
  /** Per-model default when effort is supported. */
  defaultEffort: EffortLevel | null;
};

// Capability table — ground truth is the skill's model docs. Kept small
// and explicit so additions land in one place.
const CAPABILITIES: Record<ModelId, ModelCapabilities> = {
  // Opus 4.7: adaptive thinking only; effort adds xhigh (between high
  // and max). xhigh is Anthropic's recommendation for coding / agentic.
  'claude-opus-4-7': {
    adaptiveThinking: true,
    efforts: ['off', 'low', 'medium', 'high', 'xhigh', 'max'],
    defaultEffort: 'xhigh',
  },
  // Opus 4.6: adaptive thinking; effort supports max but not xhigh.
  'claude-opus-4-6': {
    adaptiveThinking: true,
    efforts: ['off', 'low', 'medium', 'high', 'max'],
    defaultEffort: 'high',
  },
  // Sonnet 4.6: adaptive thinking; effort stops at high (no max).
  'claude-sonnet-4-6': {
    adaptiveThinking: true,
    efforts: ['off', 'low', 'medium', 'high'],
    defaultEffort: 'medium',
  },
  // Haiku 4.5: no effort parameter, no adaptive thinking.
  'claude-haiku-4-5': {
    adaptiveThinking: false,
    efforts: [],
    defaultEffort: null,
  },
};

export function capabilitiesFor(model: ModelId): ModelCapabilities {
  return CAPABILITIES[model];
}

export type ModelOption = {
  id: ModelId;
  label: string;
  hint: string;
};

export const MODEL_OPTIONS: ModelOption[] = [
  {
    id: 'claude-opus-4-7',
    label: 'opus 4.7',
    hint: 'most capable · best for composition',
  },
  {
    id: 'claude-opus-4-6',
    label: 'opus 4.6',
    hint: 'previous-gen opus · similar quality',
  },
  {
    id: 'claude-sonnet-4-6',
    label: 'sonnet 4.6',
    hint: 'fast · cheaper · good for quick edits',
  },
  {
    id: 'claude-haiku-4-5',
    label: 'haiku 4.5',
    hint: 'fastest · cheapest · descriptions & Q&A',
  },
];

export const DEFAULT_MODEL: ModelId = 'claude-opus-4-7';

function isModelId(v: unknown): v is ModelId {
  return (
    typeof v === 'string' &&
    MODEL_OPTIONS.some((m) => m.id === v)
  );
}

function isEffortLevel(v: unknown): v is EffortLevel {
  return (
    v === 'off' ||
    v === 'low' ||
    v === 'medium' ||
    v === 'high' ||
    v === 'xhigh' ||
    v === 'max'
  );
}

export function getModel(): ModelId {
  try {
    const raw = localStorage.getItem(MODEL_KEY);
    return isModelId(raw) ? raw : DEFAULT_MODEL;
  } catch {
    return DEFAULT_MODEL;
  }
}

export function setModel(id: ModelId): void {
  try {
    localStorage.setItem(MODEL_KEY, id);
  } catch {
    // private-mode / quota — in-memory only is fine.
  }
}

/** Return the stored effort clamped to the model's capability set.
 *  If the stored effort isn't valid for this model (e.g. stored "max"
 *  but model switched to Sonnet 4.6), falls through to the model's
 *  defaultEffort. Returns null when the model doesn't support effort
 *  at all (Haiku). */
export function getEffort(model: ModelId): EffortLevel | null {
  const caps = CAPABILITIES[model];
  if (caps.efforts.length === 0) return null;
  try {
    const raw = localStorage.getItem(EFFORT_KEY);
    if (isEffortLevel(raw) && caps.efforts.includes(raw)) return raw;
  } catch {
    // ignore
  }
  return caps.defaultEffort;
}

export function setEffort(effort: EffortLevel): void {
  try {
    localStorage.setItem(EFFORT_KEY, effort);
  } catch {
    // ignore
  }
}

// SECURITY INVARIANT — the single most important property of this
// codebase:
//
//   The Anthropic API key is NEVER sent to our backend. Not to the
//   Cloudflare Worker, not to Supabase, not anywhere that isn't
//   api.anthropic.com directly.
//
// The SDK is constructed client-side with `dangerouslyAllowBrowser:
// true` (see agent/client.ts) precisely so the browser fetches Claude
// directly. If a future refactor pushes LLM calls through a server,
// this comment must be revisited + the user's key handling explicitly
// migrated (server-stored per-user keys, opt-in, encrypted at rest).
// Don't quietly change the trust model.

export function getAnthropicKey(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setAnthropicKey(key: string): void {
  const trimmed = key.trim();
  if (!trimmed) {
    localStorage.removeItem(KEY);
    return;
  }
  localStorage.setItem(KEY, trimmed);
}

export function clearAnthropicKey(): void {
  localStorage.removeItem(KEY);
}

export function maskKey(key: string | null): string {
  if (!key) return '';
  if (key.length <= 12) return '•'.repeat(key.length);
  return `${key.slice(0, 8)}…${key.slice(-4)}`;
}
